import { useFormik } from "formik";
import { debounce } from "lodash";
import { ReactNode, useEffect, useState } from "react";
import ModalComponent from "..";
import {
  useAddPayoutAccountMutation,
  useGetBankListQuery,
  useResolveAccountDetailsMutation,
  useUpdatePayoutAccountMutation,
} from "../../../redux/features/payment/payment.api";
import { PRIMARY_COLOR } from "../../../utils/constants";
import { BaseApiErrorResponse, ModalCompProps } from "../../../utils/types";
import { AddPayoutSchema } from "../../../utils/validations";
import { Button } from "../../buttons/button";
import { InfoCard } from "../../cards/infoCard";
import ComboBox from "../../dropdown/comboBox";
import { Icon } from "../../icon/icon";
import { TextInput } from "../../inputs/textInput";
import { LoadingOval } from "../../loaders/loaders";

const handleSearch = async (searchFunction: () => void) => {
  await searchFunction();
};

const handleDebouncedSearch = debounce(handleSearch, 800);

export const PayoutForm = ({
  children,
  className = "",
  onSuccess = () => {},
  mode = "create",
}: {
  children: (props: {
    handleSubmit: () => void;
    loading: boolean;
  }) => ReactNode;
  className?: string;
  onSuccess?: () => void;
  mode?: "create" | "edit";
}) => {
  const [bankSearch, setBankSearch] = useState("");

  const { data: bankList } = useGetBankListQuery({});

  const [
    resolveAccount,
    {
      data: accountDetails,
      isSuccess: resolveSuccess,
      isError: resolveError,
      error: resolveErrorDetails,
      isLoading: resolvingAccount,
    },
  ] = useResolveAccountDetailsMutation();

  const [addPayout, { isLoading: addingPayout }] =
    useAddPayoutAccountMutation();

  const [updatePayout, { isLoading: updatingPayout }] =
    useUpdatePayoutAccountMutation();

  const {
    handleSubmit,
    values,
    touched,
    handleBlur,
    handleChange,
    errors,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      accountName: "",
      accountNumber: "",
      bankName: "",
      bankCode: "",
    },
    validationSchema: AddPayoutSchema,
    onSubmit: async (vals) => {
      if (resolveSuccess && accountDetails.data) {
        if (mode === "create") {
          const resp = await addPayout({
            accountName: accountDetails.data.accountName,
            accountNumber: accountDetails.data.accountNumber,
            bankName: vals.bankName,
            bankCode: accountDetails.data.bankCode,
          });

          if (resp.data?.status === "success") {
            onSuccess();
          }
        } else {
          const resp = await updatePayout({
            accountName: accountDetails.data.accountName,
            accountNumber: accountDetails.data.accountNumber,
            bankName: vals.bankName,
            bankCode: accountDetails.data.bankCode,
          });

          if (resp.data?.status === "success") {
            onSuccess();
          }
        }
      }
    },
  });

  useEffect(() => {
    if (values.accountNumber !== "" && values.bankCode !== "") {
      handleDebouncedSearch(async () => {
        await resolveAccount({
          accountNumber: values.accountNumber,
          bankCode: values.bankCode,
        });
      });
    }
    // eslint-disable-next-line
  }, [values.accountNumber, values.bankCode]);

  useEffect(() => {
    return () => {
      resetForm();
    };
    // eslint-disable-next-line
  }, []);

  return (
    <form
      onSubmit={handleSubmit}
      className={`grid grid-cols-1 gap-4 ${className}`}
    >
      <ComboBox
        menuItems={bankList?.data
          ?.filter((bank) =>
            bankSearch !== ""
              ? bank.name.toLowerCase().includes(bankSearch.toLowerCase())
              : true,
          )
          ?.map((bank) => {
            return {
              name: bank.name,
              value: { name: bank.name, code: bank.code },
            };
          })}
        position={"bottom start"}
        menuClassName="translate-x-[-12px] !h-[224px] !z-[999]"
        menuItemsClassName=""
        onSearch={(search) => {
          setBankSearch(search);
        }}
        onSelect={(val: { name: string; code: string }) => {
          setFieldValue("bankName", val.name);
          setFieldValue("bankCode", val.code);
        }}
        onMenuClose={() => {
          setBankSearch("");
        }}
        immediate
      >
        <TextInput
          type="text"
          name="bankName"
          value={values.bankName}
          placeholder="Select Bank"
          onChange={(e) => {
            handleChange(e);
            setFieldValue("bankCode", "");
          }}
          onBlur={handleBlur}
          hasTitle
          title="Bank"
          readOnly={false}
          customIconPosition="right"
          errors={
            (errors.bankName && touched.bankName ? errors.bankName : "") ||
            (errors.bankCode && touched.bankName ? errors.bankCode : "")
          }
          inputClass="capitalize"
        />
      </ComboBox>

      <TextInput
        type="text"
        name="accountNumber"
        value={values.accountNumber}
        placeholder="Account Number"
        onChange={handleChange}
        onBlur={handleBlur}
        hasTitle
        title="Account Number"
        errors={
          (errors.accountNumber && touched.accountNumber
            ? errors.accountNumber
            : undefined) ??
          (resolveError
            ? (resolveErrorDetails as unknown as BaseApiErrorResponse)?.data
                ?.message
            : undefined)
        }
        customIconPosition="right"
        customIcon={
          resolvingAccount ? (
            <LoadingOval
              color={PRIMARY_COLOR}
              loaderWidth="20"
              loaderHeight="20"
            />
          ) : undefined
        }
      />

      {resolveSuccess && (
        <TextInput
          type="text"
          name="accountName"
          value={accountDetails.data.accountName}
          placeholder="Account Name"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Account Name"
          customIconPosition="right"
          customIcon={
            <Icon
              className="text-success"
              svg="circle-check"
              width={20}
              height={20}
            />
          }
          readOnly
        />
      )}

      {children({ handleSubmit, loading: addingPayout || updatingPayout })}
    </form>
  );
};

export const AddPayoutAccountModal = ({
  open,
  handleClose,
  onSuccess = () => {},
  mode = "create",
}: ModalCompProps & {
  onSuccess: () => void;
  mode?: "create" | "edit";
}) => {
  const close = () => {
    handleClose();
  };

  return (
    <ModalComponent
      open={open}
      handleClose={close}
      className="!p-"
      dialogClassName=""
      closeOnOverlayClick
      showHeader
      title={`${mode === "create" ? "Add" : "Update"} Bank Details`}
      subtitle={""}
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <div className="flex flex-col gap-4">
        <InfoCard
          leftIcon="info"
          title="You can only add one payout account at a time"
          text="Your payout account should be the same name as your profile name"
          className="md:!items-start"
        />

        <PayoutForm onSuccess={onSuccess} mode={mode}>
          {({ handleSubmit, loading }) => {
            return (
              <div className="col-span-full grid w-full grid-cols-2 items-center justify-end gap-2 md:flex">
                <Button
                  text="Cancel"
                  type="button"
                  onClick={() => {
                    close();
                  }}
                  variation="plain-grey"
                  className="md:max-w-fit"
                />
                <Button
                  text={"Save"}
                  type="button"
                  onClick={handleSubmit}
                  className="md:max-w-fit"
                  loading={loading}
                  disabled={loading}
                />
              </div>
            );
          }}
        </PayoutForm>
      </div>
    </ModalComponent>
  );
};
